import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RackingArea } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { calcTotalRackFramesDepth } from '@/modules/common/helpers/rack';
import { NumUtils } from '@/modules/common/utils';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { PropertiesPopup } from '@/modules/common/components/PropertiesPopup';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { AccordionPreferenceIconButton } from '@/modules/common/components/AccordionPreferenceIconButton';
import { useDebouncedCallback } from '@/modules/common/hooks';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { isAtLeast1 } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useAccordionExpanded } from '@/modules/userPreferences/hooks';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import { UserPreferenceName } from '@/modules/userPreferences';
import { FIELDS_PADDING_BOTTOM, FIELDS_SPACING } from '@/modules/common/constants/propertyPanel';

enum InputName {
  BEAM_HEIGHT = 'beam_height',
  CLEARANCE_TOP = 'clearance_top',
  CLEARANCE_IN_BETWEEN = 'clearance_in_between',
  CLEARANCE_SIDE = 'clearance_side',
  FIRST_SHELF_HEIGHT = 'first_shelf_height',
}

const mapInputToImage = (inputName: InputName | null) => {
  switch (inputName) {
    case InputName.CLEARANCE_SIDE:
      return '/racking/side-margin.png';
    case InputName.CLEARANCE_TOP:
      return '/racking/top-margin.png';
    case InputName.CLEARANCE_IN_BETWEEN:
      return '/racking/gap-between-loads.png';
    case InputName.BEAM_HEIGHT:
      return '/racking/beam.png';
    case InputName.FIRST_SHELF_HEIGHT:
      return '/racking/first-shelf-height.png';
    default:
      return '/racking/default.png';
  }
};

type RackSpecType = RackingArea['rackSpec'];
type SetupLoadType = { id: string; width: number };

interface Props {
  selectedRackingAreas: RackingArea[];
}

/** @description Width of the rack's first load type, or 0 when it is not in the setup. */
const getLoadTypeSize = (rackSpec: RackSpecType, loadTypes: SetupLoadType[]): number =>
  loadTypes.find((loadType) => loadType.id === rackSpec.loadTypes[0].loadTypeId)?.width ?? 0;

/** @description The widest load type the rack uses. */
const getLoadWidth = (rackSpec: RackSpecType, loadTypes: SetupLoadType[]): number => {
  const rackingLoadTypeIds = new Set(rackSpec.loadTypes.map((loadType) => loadType.loadTypeId));
  const rackingLoadTypes = loadTypes.filter((loadType) => rackingLoadTypeIds.has(loadType.id));

  return Math.max(...rackingLoadTypes.map((lt) => lt.width));
};

/** @description Recalculates beam and bay length after a column or clearance change. */
const withBeamAndBayLength = (rackSpec: RackSpecType, loadWidth: number): RackSpecType => {
  const { numColumnsInBay, clearanceSide, clearanceInBetween } = rackSpec;
  const beamLength =
    numColumnsInBay * loadWidth + 2 * clearanceSide + (numColumnsInBay - 1) * clearanceInBetween;
  const currentPostWidth = (rackSpec.bayLength - rackSpec.beamLength) / 2;

  return { ...rackSpec, beamLength, bayLength: beamLength + 2 * currentPostWidth };
};

export const RackSpec = ({ selectedRackingAreas }: Props) => {
  const { t } = useTranslation();
  const loadTypes = useFloorplanDesignStore((state) => state.setup.loadTypes);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const [focusedInputName, setFocusedInputName] = useState(null);
  const [image, setImage] = useState('/racking/default.png');
  const { expanded: accordionOpen = false, setExpanded: setAccordionOpen } = useAccordionExpanded(
    UserPreferenceName.ACCORDION_PROPERTY_PANEL_RACKING_RACK_SPEC,
  );
  const [popupOpen, setPopupOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { callback: setImageDebounced } = useDebouncedCallback(
    (value: string) => setImage(value),
    50,
  );

  useEffect(() => {
    setImageDebounced(mapInputToImage(focusedInputName));
  }, [focusedInputName, setImageDebounced]);

  const handleSetAccordionOpen = useCallback(
    (expanded: boolean) => {
      setAccordionOpen(expanded);
    },
    [setAccordionOpen],
  );

  const onPopupOpen = useCallback(() => {
    setPopupOpen(true);
    setAccordionOpen(true);
  }, [setAccordionOpen]);

  const onPopupClose = useCallback(() => {
    setPopupOpen(false);
  }, []);

  const onFocus = useCallback(
    (evt) => {
      setFocusedInputName(evt.target.name);
    },
    [setFocusedInputName],
  );

  const onBlur = useCallback(() => {
    setFocusedInputName(null);
  }, [setFocusedInputName]);

  // Racks whose load type is in the setup: the spec needs its size to recalculate the rack.
  // Shown when any selected rack has one, only editable when all of them do.
  const sizedRacks = useMemo(
    () => selectedRackingAreas.filter(({ rackSpec }) => getLoadTypeSize(rackSpec, loadTypes) > 0),
    [loadTypes, selectedRackingAreas],
  );
  const isPartialSelection = sizedRacks.length !== selectedRackingAreas.length;
  const disabledReasonKey = isPartialSelection
    ? 'interface:properties.area.rack.partial_selection.only_known_load_types'
    : undefined;

  // null means the selected racks have different values ('Mixed')
  const commonValues = useMemo(() => {
    const rackSpecs = selectedRackingAreas.map(({ rackSpec }) => rackSpec);
    const getCommonMm = (values: number[]) =>
      getCommonNumber(values.map((value) => NumUtils.metersToMmRounded(value)));

    return {
      levels: getCommonNumber(rackSpecs.map(({ shelfHeights }) => shelfHeights.length)),
      numColumnsInBay: getCommonNumber(rackSpecs.map((spec) => spec.numColumnsInBay)),
      numStationsInDepth: getCommonNumber(rackSpecs.map((spec) => spec.numStationsInDepth)),
      clearanceSideMm: getCommonMm(rackSpecs.map((spec) => spec.clearanceSide)),
      clearanceInBetweenMm: getCommonMm(rackSpecs.map((spec) => spec.clearanceInBetween)),
      clearanceTopMm: getCommonMm(rackSpecs.map((spec) => spec.clearanceTop)),
      beamHeightMm: getCommonMm(rackSpecs.map((spec) => spec.beamHeight)),
      firstShelfHeightMm: getCommonMm(rackSpecs.map(({ shelfHeights }) => shelfHeights[0])),
    };
  }, [selectedRackingAreas]);

  /**
   * Applies a rack spec change to every selected rack in one update (one undo step).
   * Returns the first updated rack spec, e.g. to store derived values as preference.
   */
  const updateRackSpecs = useCallback(
    (updateSpec: (rackSpec: RackSpecType) => RackSpecType | null): RackSpecType | undefined => {
      if (isPartialSelection) return undefined;

      const updatedAreas = createEmptyAreas();
      let firstUpdatedSpec: RackSpecType | undefined;

      selectedRackingAreas.forEach((area) => {
        const rackSpec = updateSpec(area.rackSpec);
        if (!rackSpec) return;

        firstUpdatedSpec ??= rackSpec;
        pushAreaToItsCollection(updatedAreas, { ...area, rackSpec });
      });

      fddmSvc.updateAreas(updatedAreas);
      return firstUpdatedSpec;
    },
    [fddmSvc, isPartialSelection, selectedRackingAreas],
  );

  /** Stores the recalculated beam and bay length as preference, like a single-rack edit. */
  const setBeamAndBayPreferences = useCallback(
    (rackSpec: RackSpecType | undefined) => {
      if (!rackSpec) return;

      fddmSvc.setPreference('rackingArea.rackSpec.beamLength', rackSpec.beamLength);
      fddmSvc.setPreference('rackingArea.rackSpec.bayLength', rackSpec.bayLength);
    },
    [fddmSvc],
  );

  const onLevelsChange = useCallback(
    (value: number) => {
      updateRackSpecs((rackSpec) => {
        const newArray = Array.from({ length: value }, (_, i) => rackSpec.shelfHeights[0] + i * 1);
        return isAtLeast1(newArray) ? { ...rackSpec, shelfHeights: newArray } : null;
      });
    },
    [updateRackSpecs],
  );

  const onBeamHeightChange = useCallback(
    (valueMm: number) => {
      const beamHeight = NumUtils.mmToMeters(valueMm);

      fddmSvc.setPreference('rackingArea.rackSpec.beamHeight', beamHeight);
      updateRackSpecs((rackSpec) => ({ ...rackSpec, beamHeight }));
    },
    [fddmSvc, updateRackSpecs],
  );

  const onClearanceSideChange = useCallback(
    (valueMm: number) => {
      const valueSanitized = NumUtils.mmToMeters(valueMm);
      const setupLoadTypes = useFloorplanDesignStore.getState().setup.loadTypes;

      fddmSvc.setPreference('rackingArea.rackSpec.clearanceSide', valueSanitized);
      const firstUpdatedSpec = updateRackSpecs((rackSpec) =>
        withBeamAndBayLength(
          { ...rackSpec, clearanceSide: valueSanitized },
          getLoadWidth(rackSpec, setupLoadTypes),
        ),
      );
      setBeamAndBayPreferences(firstUpdatedSpec);
    },
    [fddmSvc, setBeamAndBayPreferences, updateRackSpecs],
  );

  const onClearanceTopChange = useCallback(
    (valueMm: number) => {
      const clearanceTop = NumUtils.mmToMeters(valueMm);

      fddmSvc.setPreference('rackingArea.rackSpec.clearanceTop', clearanceTop);
      updateRackSpecs((rackSpec) => ({ ...rackSpec, clearanceTop }));
    },
    [fddmSvc, updateRackSpecs],
  );

  const onClearanceInBetweenChange = useCallback(
    (valueMm: number) => {
      const valueSanitized = NumUtils.mmToMeters(valueMm);
      const setupLoadTypes = useFloorplanDesignStore.getState().setup.loadTypes;

      fddmSvc.setPreference('rackingArea.rackSpec.clearanceInBetween', valueSanitized);
      const firstUpdatedSpec = updateRackSpecs((rackSpec) =>
        withBeamAndBayLength(
          { ...rackSpec, clearanceInBetween: valueSanitized },
          getLoadWidth(rackSpec, setupLoadTypes),
        ),
      );
      setBeamAndBayPreferences(firstUpdatedSpec);
    },
    [fddmSvc, setBeamAndBayPreferences, updateRackSpecs],
  );

  const onNumColumnsInBayChange = useCallback(
    (value: number) => {
      const setupLoadTypes = useFloorplanDesignStore.getState().setup.loadTypes;

      fddmSvc.setPreference('rackingArea.rackSpec.numColumnsInBay', value);
      const firstUpdatedSpec = updateRackSpecs((rackSpec) =>
        withBeamAndBayLength(
          { ...rackSpec, numColumnsInBay: value },
          getLoadWidth(rackSpec, setupLoadTypes),
        ),
      );
      setBeamAndBayPreferences(firstUpdatedSpec);
    },
    [fddmSvc, setBeamAndBayPreferences, updateRackSpecs],
  );

  const onNumStationsInDepthChange = useCallback(
    (value: number) => {
      const setupLoadTypes = useFloorplanDesignStore.getState().setup.loadTypes;

      fddmSvc.setPreference('rackingArea.rackSpec.numStationsInDepth', value);
      updateRackSpecs((rackSpec) => ({
        ...rackSpec,
        numStationsInDepth: value,
        bayDepth: calcTotalRackFramesDepth(
          getLoadTypeSize(rackSpec, setupLoadTypes),
          value,
          rackSpec.clearanceDepth + rackSpec.overhangFront + rackSpec.overhangBack,
        ),
      }));
    },
    [fddmSvc, updateRackSpecs],
  );

  const onFirstShelfHeightChange = useCallback(
    (value: number) => {
      updateRackSpecs((rackSpec) => {
        const newArray = Array.from(
          { length: rackSpec.shelfHeights.length },
          (_, i) => NumUtils.mmToMeters(value) + i * 1,
        );
        return isAtLeast1(newArray) ? { ...rackSpec, shelfHeights: newArray } : null;
      });
    },
    [updateRackSpecs],
  );

  if (sizedRacks.length === 0) return null;

  return (
    <>
      <Accordion
        title={t<string>('interface:properties.area.rack_spec.label', 'Racking Spec')}
        unmountOnExit
        expanded={accordionOpen}
        onChange={handleSetAccordionOpen}
        defaultExpanded
        ref={panelRef}
        afterIcon={
          <AccordionPreferenceIconButton
            tooltip={t('interface:properties.area.rack.racking_preferences')}
            onAdd={onPopupOpen}
            tooltipWhenDisabled={false}
          />
        }
      >
        <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
          <PropertiesLabel i18nkey='interface:properties.area.rack_spec.levels'>
            <DisabledReasonTooltip reasonKey={disabledReasonKey}>
              <NumberInput
                disabled={isPartialSelection ? true : undefined}
                value={commonValues.levels}
                sx={{ width: '100%' }}
                minValue={1}
                onChange={onLevelsChange}
              />
            </DisabledReasonTooltip>
          </PropertiesLabel>

          <PropertiesLabel i18nkey='interface:properties.area.rack_spec.num_Columns_In_Bay'>
            <DisabledReasonTooltip reasonKey={disabledReasonKey}>
              <NumberInput
                disabled={isPartialSelection ? true : undefined}
                value={commonValues.numColumnsInBay}
                sx={{ width: '100%' }}
                onChange={onNumColumnsInBayChange}
                minValue={1}
              />
            </DisabledReasonTooltip>
          </PropertiesLabel>

          <PropertiesLabel i18nkey='interface:properties.area.rack_spec.num_stations_in_depth'>
            <DisabledReasonTooltip reasonKey={disabledReasonKey}>
              <NumberInput
                disabled={isPartialSelection ? true : undefined}
                value={commonValues.numStationsInDepth}
                sx={{ width: '100%' }}
                onChange={onNumStationsInDepthChange}
                minValue={1}
              />
            </DisabledReasonTooltip>
          </PropertiesLabel>
        </Stack>
      </Accordion>
      <PropertiesPopup
        anchor={panelRef}
        open={popupOpen}
        onClose={onPopupClose}
        header={
          <PropertiesTitle
            fontSize={14}
            fontWeight={400}
            value={t('interface:properties.area.rack.racking_preferences')}
          />
        }
        sx={{
          width: '320px',
          height: 'auto',
        }}
      >
        <Stack gap={0}>
          <Stack alignItems='center'>
            {image && <img src={image} alt='' width={203} height={223} />}
          </Stack>

          <Stack gap={1} px={2}>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.clearance_side'
            >
              <DisabledReasonTooltip reasonKey={disabledReasonKey}>
                <NumberInput
                  disabled={isPartialSelection ? true : undefined}
                  value={commonValues.clearanceSideMm}
                  onChange={onClearanceSideChange}
                  name={InputName.CLEARANCE_SIDE}
                  sx={{ width: '100%' }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  unit='mm'
                  minValue={0}
                />
              </DisabledReasonTooltip>
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.clearance_in_between'
            >
              <DisabledReasonTooltip reasonKey={disabledReasonKey}>
                <NumberInput
                  disabled={isPartialSelection ? true : undefined}
                  value={commonValues.clearanceInBetweenMm}
                  onChange={onClearanceInBetweenChange}
                  name={InputName.CLEARANCE_IN_BETWEEN}
                  sx={{ width: '100%' }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  unit='mm'
                  minValue={0}
                />
              </DisabledReasonTooltip>
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.clearance_top'
            >
              <DisabledReasonTooltip reasonKey={disabledReasonKey}>
                <NumberInput
                  disabled={isPartialSelection ? true : undefined}
                  value={commonValues.clearanceTopMm}
                  onChange={onClearanceTopChange}
                  name={InputName.CLEARANCE_TOP}
                  sx={{ width: '100%' }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  unit='mm'
                  minValue={0}
                />
              </DisabledReasonTooltip>
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack_spec.beam_height'
            >
              <DisabledReasonTooltip reasonKey={disabledReasonKey}>
                <NumberInput
                  disabled={isPartialSelection ? true : undefined}
                  value={commonValues.beamHeightMm}
                  onChange={onBeamHeightChange}
                  name={InputName.BEAM_HEIGHT}
                  sx={{ width: '100%' }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  unit='mm'
                  minValue={1}
                />
              </DisabledReasonTooltip>
            </PropertiesLabel>
            <PropertiesLabel
              labelRatio={60}
              i18nkey='interface:properties.area.rack.first_shelf_height'
            >
              <DisabledReasonTooltip reasonKey={disabledReasonKey}>
                <NumberInput
                  disabled={isPartialSelection ? true : undefined}
                  value={commonValues.firstShelfHeightMm}
                  onChange={onFirstShelfHeightChange}
                  name={InputName.FIRST_SHELF_HEIGHT}
                  sx={{ width: '100%' }}
                  onBlur={onBlur}
                  onFocus={onFocus}
                  unit='mm'
                  minValue={0}
                />
              </DisabledReasonTooltip>
            </PropertiesLabel>
          </Stack>
        </Stack>
      </PropertiesPopup>
    </>
  );
};
